import { useInfiniteQuery, useQuery } from "react-query";

import { APIhandler } from "../../Utils/api";

import { getTransformedActivities, getTransformedRentPaymentTransaction } from "../../Utils/api-data-transforms";
import { getAcronym, getUserId, mapSsoPrefillToStakeSaverFormPrefillShape } from "../../Utils/helpers";
import { normalizeSsoPrefillFlatPayload } from "../../Utils/sso-prefill-normalize";
import { APIQueryKeys } from "../../Utils/constants";

export const useMobileAppSummary = (config) => {
  return useQuery(APIQueryKeys.mobileAppSummary, () => APIhandler.platformSummary.getMobileAppSummary(), config);
};

export const usePlatformSummaryStats = (config) => {
  return useQuery(
    APIQueryKeys.platformSummaryStats,
    () => APIhandler.platformSummary.getPlatformSummaryStats(),
    config,
  );
};

export const useDeals = (config) => {
  return useQuery(APIQueryKeys.deals, () => APIhandler.deals.requestDeals(), config);
};

export const useConsumerDeals = (config) => {
  return useQuery(APIQueryKeys.consumerDeals, () => APIhandler.deals.consumerDeals(), config);
};

export const useCustomerDetailsForInsurance = (config) => {
  return useQuery(APIQueryKeys.customerInsuranceDetails, () => APIhandler.insurance.getCustomerDetailsForInsurance(), {
    select: ({ data }) => data?.data,
    ...config,
  });
};

export const useInsurancePolicyDetails = (config) => {
  return useQuery(APIQueryKeys.insurancePolicyDetails, () => APIhandler.insurance.getInsurancePolicyDetails(), {
    select: ({ data }) => data?.data,
    ...config,
  });
};

export const useUnclaimedTransactions = (config) => {
  return useQuery(
    APIQueryKeys.unclaimedTransactions,
    () => APIhandler.unclaimedTransactions.getUnclaimedTransactions(),
    config,
  );
};

export const useTransferToken = (config) => {
  return useQuery(APIQueryKeys.transferToken, () => APIhandler.tokens.getTransferToken(), config);
};

export const useAlternativeLoginFlag = (config) => {
  return useQuery(
    APIQueryKeys.alternativeLoginFlag,
    () => APIhandler.alternativeLogin.getAlternativeLoginFlag(),
    config,
  );
};

export const useConnectedAccounts = (config) => {
  return useQuery(
    APIQueryKeys.connectedAccounts,
    (data) => APIhandler.bankConnections.getConnectedAcccounts(data),
    config,
  );
};

export const useConnectedAccountSelection = (config) => {
  return useQuery(
    APIQueryKeys.connectedAccountsSelection,
    (data) => APIhandler.bankConnections.getConnectedAcccountSelection(data),
    config,
  );
};

export const useStakeDebit = (config) => {
  return useQuery(APIQueryKeys.stakeDebit, () => APIhandler.stakeDebit.getStakeDebit(), config);
};

export const useStakeDebitStatements = (config) => {
  return useQuery(APIQueryKeys.stakeDebitStatements, () => APIhandler.stakeDebit.getStakeDebitStatements(), config);
};

export const usePayrollAccounts = (config) => {
  return useQuery(APIQueryKeys.payrollAccount, () => APIhandler.payroll.getPayrollAccounts(), config);
};

export const useCreditReportDetails = (config) => {
  return useQuery(APIQueryKeys.creditReporting, () => APIhandler.creditBuilder.getCreditReportDetails(), config);
};

export const useUserAccount = (config) => {
  return useQuery(APIQueryKeys.userAccount, () => APIhandler.resident.getUserAccount(), config);
};

export const useActivities = (config) => {
  return useQuery(APIQueryKeys.activities, () => APIhandler.ledger.getActivities(), {
    select: (response) => getTransformedActivities(response),
    ...config,
  });
};

export const useCheckConfig = (config) => {
  return useQuery(APIQueryKeys.checkConfig, () => APIhandler.check.getCheckConfig(getUserId()), config);
};

export const useSendVerificationCodeUsingUniqueID = (uniqueContactID, config) => {
  return useQuery(
    APIQueryKeys.sendVerificationCodeUsingUniqueID,
    () => APIhandler.login.sendVerificationCode(uniqueContactID),
    config,
  );
};

export const useRentDetails = (config) => {
  return useQuery(APIQueryKeys.rentDetails, () => APIhandler.resident.getRentDetails(), config);
};

// New Astra query endpoints
export const useAstraCards = (config = {}) => {
  return useQuery(APIQueryKeys.astraCards, () => APIhandler.astra.fetchAstraCards(), config);
};

export const useFetchAstraAccounts = (config = {}) => {
  return useQuery(APIQueryKeys.astraAccounts, () => APIhandler.astra.fetchAstraAccounts(), config);
};

export const useCheckRoutesCapabilities = (config = {}) => {
  return useQuery(APIQueryKeys.routesCapabilities, () => APIhandler.astra.checkRoutesCapabilities(), config);
};

// Recurring Deposit
export const useGetRecurringDeposit = (config = {}) => {
  return useQuery(APIQueryKeys.recurringDeposit, () => APIhandler.recurringDeposits.getRecurringDeposit(), config);
};

//StakePay 3

export const useStakePay3Auth = (sfid, config) => {
  return useQuery([...APIQueryKeys.stakePay3Auth, sfid], () => APIhandler.stakepay3.getAuthToken(sfid), config);
};

export const useStakePay3PaymentDetails = (config) => {
  return useQuery(APIQueryKeys.stakePay3PaymentDetails, () => APIhandler.stakepay3.getPaymentDetails(), config);
};

export const useFinixPublicKey = (config = {}) => {
  return useQuery(APIQueryKeys.finixPublicKey, () => APIhandler.finix.getFinixPublicKey(), config);
};

export const useStripePublicKey = (params, config = {}) => {
  return useQuery(APIQueryKeys.stripePublicKey, () => APIhandler.stripe.getStripePublicKey(params), config);
};

export const useFinixConnectedCards = (config = {}) => {
  return useQuery(APIQueryKeys.finixConnectedCards, () => APIhandler.finix.getCards(), config);
};

export const useStakePay3PaymentMonths = (sfid, config) => {
  return useQuery(
    [...APIQueryKeys.stakePay3PaymentMonths, sfid],
    () => APIhandler.stakepay3.getPaymentMonths(sfid),
    config,
  );
};

export const useStakePay3PaymentSetupSummary = (sfid, config) => {
  return useQuery(
    [...APIQueryKeys.stakePay3PaymentSetupSummary, sfid],
    () => APIhandler.stakepay3.getPaymentSetupSummary(sfid),
    config,
  );
};

export const useAutopayPaymentSetupSummary = (sfid, config) => {
  return useQuery(
    [...APIQueryKeys.stakePay3AutopayPaymentSetupSummary, sfid],
    () => APIhandler.stakepay3.getAutopayPaymentSetupSummary(sfid),
    config,
  );
};

export const useStakePay3PaymentDetailsForSelectedMonth = (contractPaymentId, config) => {
  return useQuery(
    [...APIQueryKeys.stakePay3PaymentDetailsForSelectedMonth, contractPaymentId],
    () => APIhandler.stakepay3.getPaymentDetailsForSelectedMonth(contractPaymentId),
    config,
  );
};

export const useStakePay3PastPayments = (sfid, config) => {
  return useQuery(
    [sfid, ...APIQueryKeys.stakePay3PastPayments],
    () => APIhandler.stakepay3.getPastPayments(sfid),
    config,
  );
};

export const useConsumerPaymentMethods = (config) => {
  return useQuery(
    APIQueryKeys.consumerPaymentMethods,
    () => APIhandler.consumerPaymentMethods.getConsumerPaymentMethods(),
    config,
  );
};

export const useIsCustomerFromPartnerOnboarded = (payload, config) => {
  return useQuery(
    APIQueryKeys.isCustomerFromPartnerOnboarded,
    () => APIhandler.partnerSignUp.isCustomerFromPartnerOnboarded(payload),
    config,
  );
};

export const useGetPartnerCustomerInformation = (payload, config) => {
  return useQuery(
    APIQueryKeys.getPartnerCustomerInformation,
    () => APIhandler.partnerSignUp.getPartnerCustomerInformation(payload),
    config,
  );
};

export const useOnPartnerOnboardingLoad = (partnerId, config) => {
  return useQuery(
    [partnerId, ...APIQueryKeys.onPartnerOnboardingLoad],
    () => APIhandler.partnerSignUp.onPartnerOnboardingLoad(partnerId),
    config,
  );
};

export const useGetFidelPublicKey = (config) => {
  return useQuery(APIQueryKeys.fidelPublicKey, () => APIhandler.fidel.getFidelPublicKey(), {
    select: ({ data }) => data?.data?.fidel_public_key,
    ...config,
  });
};

export const useGetFidelLinkData = (config) => {
  return useQuery(APIQueryKeys.fidelLinkData, () => APIhandler.fidel.getLinkData(), {
     select: ({ data }) => data?.data,
    ...config,
  });
};

export const useStakeSaverFormPrefill = (config) => {
  return useQuery(APIQueryKeys.ssoPrefill, () => APIhandler.sso.getPrefill(), {
    select: (res) => mapSsoPrefillToStakeSaverFormPrefillShape(res?.data?.data),
    ...config,
  });
};

export const useGetFidelCards = (config) => {
  return useQuery(APIQueryKeys.fidelCards, () => APIhandler.fidel.getFidelCards(), {
    select: ({ data }) => data?.data,
    ...config,
  });
};

export const useGetFidelNeighborhoodCash = (config) => {
  return useQuery(APIQueryKeys.fidelNeighborhoodCash, () => APIhandler.fidel.getNeighborhoodCash(), {
    select: ({ data }) => data?.data?.rewards,
    ...config,
  });
};

export const useGetPayees = (config) => {
  return useQuery(APIQueryKeys.payeeList, () => APIhandler.payee.getPayees(), {
    select: ({ data }) => data?.data?.map((payee) => ({ ...payee, nameAcronym: getAcronym(payee.name) })),
    ...config,
  });
};

export const useRentPayments = (params = {}, config) => {
  return useQuery([...APIQueryKeys.rentPayments, params], () => APIhandler.rentPayment.getRentPayments(params), {
    select: ({ data }) => getTransformedRentPaymentTransaction(data?.data),
    ...config,
  });
};

export const useRecurringRentPayments = (config) => {
  return useQuery(APIQueryKeys.recurringRentPayments, () => APIhandler.rentPayment.getRecurringRentPayments(), {
    select: ({ data }) => getTransformedRentPaymentTransaction(data?.data),
    ...config,
  });
};

// MyUtilities
export const useMyUtilitiesOrderDetail = (token, config) => {
  return useQuery(
    [...APIQueryKeys.myUtilitiesOrderDetail, token],
    () => APIhandler.myUtilities.getOrderDetail(token),
    config,
  );
};

export const usePrograms = (config) => {
  return useQuery(APIQueryKeys.renterPrograms, () => APIhandler.programs.getPrograms(), {
    select: ({ data }) => data?.data,
    ...config,
  });
};

export const useGetFidelNearestLocations = (payload = {}, config) => {
  return useInfiniteQuery([...APIQueryKeys.fidelNearestLocations, payload], APIhandler.fidel.getNearestLocations, {
    getNextPageParam: (lastPage) => lastPage?.data?.pagination?.next_offset,
    ...config,
  });
};
export const useAppEssentials = (config) => {
  return useQuery(APIQueryKeys.appEssentials, () => APIhandler.appEssentials.getAppEssentials(), {
    select: ({ data }) => data?.data,
    ...config,
  });
};

export const useImagePreload = (urls, config) => {
  return useQuery(urls, () => APIhandler.image.getImage(urls), config);
};

export const useBankingLimits = (config) => {
  return useQuery(APIQueryKeys.bankingLimits, () => APIhandler.stakeDebit.bankingLimits(), {
    ...config,
    select: ({ data }) => data?.data,
  });
};

export const useSsoPrefill = (config) => {
  return useQuery(APIQueryKeys.ssoPrefill, () => APIhandler.sso.getPrefill(), {
    select: ({ data }) => normalizeSsoPrefillFlatPayload(data?.data),
    ...config,
  });
};

export const useCardWidgetToken = ({ cardId, widgetType }, config) => {
  return useQuery(
    [...APIQueryKeys.cardWidgetToken, cardId, widgetType],
    () => APIhandler.stakeDebit.getCardWidgetToken({ cardId, widgetType }),
    {
      enabled: !!cardId && !!widgetType,
      select: ({ data }) => data?.data,
      staleTime: 4 * 60 * 1000,
      ...config,
    },
  );
};

export const usePublicGridStatus = (config) => {
  return useQuery(APIQueryKeys.publicgridStatus, () => APIhandler.publicgrid.getStatus(), {
    ...config,
  });
};

